package org.multiplatform.kotfolio.pages.flutter

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("/flutter/get_it")
@Composable
fun GetItPage() {
    BlogLayout(
        PageProperties(
            "Get_It",
            "GetIt allows you to register services and retrieve services from a central location them anywhere in your Flutter app."
        ),
        BlogProperties("Flutter", "Get_It")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Span(Modifier.display(DisplayStyle.Contents).toAttrs()) {
                SpanText(
                    text = "GetIt is an implementation of the classic Service Locator pattern, providing various static methods to register singletons or factories.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                SpanText(
                    text = "GetIt is a service locator that allows you to create interfaces and their implementations, and access those implementations globally, anywhere in your app.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                SpanText(
                    text = "get_it is a service locator used to get at run-time the concrete class of the abstract class we are requesting.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                SpanText(
                    text = "injectable and injectable_generator will be used to annotate and generate the get_it’s code (an official example of code generation).",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                SpanText(
                    text = "injectable when the service locator pattern is used throughout the whole application and each abstract class can have several concrete classes it is very useful not to have to think about the implementation of get_it but to let the code generation take care of it.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )
            }

            SpanText(
                text = "Why do we need Dependency Injection?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DI helps you to make your code loose coupled rather than tightly coupled. Loose coupling: When an object gets the object to be used from external sources, we call it loose coupling.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In other words, loose coupling means that the objects are independent.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Tight coupling: When a group of classes are highly dependent on one another then it is called tight coupling. By using DI pattern, we can make the code more readable, reusable, and testable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Dependency Inversion",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Dependency Inversion Principle (DIP) is one of the SOLID principles of object-oriented design.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "According to DIP, high-level modules should not depend on low-level modules, but both should depend on abstractions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In other words, the principle suggests that the direction of dependency should be towards abstractions, rather than concrete implementations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To understand high-level and low-level modules in the context of DIP, you can think of high-level modules as modules that contain the more abstract, policy-related, or business logic of an application.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "These modules typically don’t concern themselves with the specific details of how things are implemented.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "On the other hand, low-level modules are the ones that deal with the specific implementation details and are more concerned with the mechanics or specifics of how things work.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "High-level modules: These are modules that contain the business logic or application-specific rules. They are often more abstract and focus on the overall flow and functionality of the application.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                        SpanText(
                            text = "Low-level modules: These are modules that deal with the implementation details, like interacting with databases, file systems, external services, etc. They are more concrete and specific in nature.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Now, the reason why high-level modules are considered high-level and low-level modules are considered low-level in DIP is related to the direction of dependency.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "High-level modules should not depend on low-level modules because it introduces a high degree of coupling and makes the high-level module sensitive to changes in the low-level module. Instead, both high-level and low-level modules should depend on abstractions (interfaces or abstract classes), which allows for flexibility and easier maintenance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Create abstract interfaces or classes that define the interactions between high-level and low-level modules. High-level modules should depend on these abstractions, and low-level modules should implement these abstractions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

            }

            SpanText(
                text = "Example",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "High-Level Module: The TaskManager is responsible for managing the overall flow of tasks in the application. It decides how tasks should be displayed, handles user interactions, and orchestrates the business logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Low-Level Module: The TaskRepository is responsible for handling the data storage details. It interacts with a database or some other form of persistent storage to retrieve and save tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Abstraction: The Task class serves as an abstraction that both the high-level TaskManager and low-level TaskRepository depend on.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //High-Level Module
                    class TaskManager {
                      final TaskRepository _taskRepository;

                    TaskManager(this._taskRepository);
                      // High-level business logic
                      List<Task> getTasks() {
                        return _taskRepository.getAllTasks();
                      }
                      void addTask(Task task) {
                        _taskRepository.saveTask(task);
                      }
                    }
                    
                    //Low-Level Module
                    class TaskRepository {
                      // Low-level data storage details
                      List<Task> getAllTasks() {
                        // Logic to retrieve tasks from a database
                      }

                      void saveTask(Task task) {
                        // Logic to save a task to a database
                      }
                    }
                    
                    //Abstraction
                    class Task {
                      final String title;
                      final String description;

                      Task(this.title, this.description);
                    }
                    """.trimIndent()
            )



            SpanText(
                text = "Types of Dependency Injection",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Constructor Injection: Dependencies are injected through a class’s constructor.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Setter Injection: Dependencies are injected using setter methods.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Provider Injection: Dependencies are provided using a global service locator.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Constructor Injection
                    class MyService {
                      final MyDependency dependency;

                      MyService(this.dependency);

                      void doSomething() {
                        dependency.performAction();
                      }
                    }
                    
                    //Setter Injection
                    class MyService {
                      MyDependency dependency;

                      void setDependency(MyDependency newDependency) {
                        dependency = newDependency;
                      }

                      void doSomething() {
                        dependency.performAction();
                      }
                    }
                    
                    //Provider Injection
                    GetIt locator = GetIt.instance;
                    
                    void setupLocator() {
                      locator.registerSingleton<MyDependency>(MyDependencyImpl());
                    }
                    class MyService {
                      MyDependency dependency = locator<MyDependency>();
                      void doSomething() {
                        dependency.performAction();
                      }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Get_It Dependency Injection",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "GetIt also suffers from the issue of providing instances of the same type, however it does expose a name field when registering, which offers a reasonable workaround for the issue.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Another potential weakness is that you may have to manually dispose or unregister items as they are not intrinsically part of the widget tree.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lastly, some developers may not like the global nature of GetIt singletons, preferring the more restrictive scoping models of riverpod or Provider, while other developers may view this as a benefit.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Register Singleton",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is during the initialisation phase of the application, such as in the main() function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By registering a dependency as a singleton, you ensure that the same instance is shared across the entire application, reducing resource usage and maintaining consistency.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Register Lazy Singleton",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Create an instance of a dependency only when it is first accessed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the dependency is first requested, the callback function will be executed to create and return a new instance. Subsequent requests for the dependency will receive the same instance that was previously created.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Optimize memory consumption and initialization time, as the instance is only created when it is actually needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Register Factory",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Create a new instance of a dependency every time it is requested.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is useful when you want to have a new instance for each usage instead of sharing a single instance across the application.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "registerSingleton",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We use this method to register a singleton instance(one instance is created and the same instance is returned whenever we call get<T>()).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class Locator {
                      static late GetIt _i;
                      static GetIt get instance => _i;

                      Locator.setup() {
                        _i = GetIt.I;

                        /// check if things are already registered, if they are skip it.
                        /// if they are already registered it is because of end-to-end testing.
                        register(ApiTheme.instance());
                      }

                      void register<T extends Object>(T constructor) {
                        if (!_i.isRegistered<T>()) {
                          _i.registerSingleton<T>(constructor);
                        }
                      }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "registerLazySingleton(FactoryFunc<T> factoryFunc)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This method is also used to register a singleton instance but it does that lazily, the object is instantiated only when get<T>() is called the first time, whereas the registerSingleton method instantiates the instance when the setUp() method is called and make the instance readily available.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "registerFactory(FactoryFunc<T> factoryFunc)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We use this method to return a new instance of type T every time we call get<T>()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "registerFactoryParam(FactoryFunc<T,P1,P2> factoryFunc)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using this method we can instantiate instances that take at most 2 parameters in their constructor.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    serviceLocator.registerFactoryParam<Person,String,int>  ((name, age) => Person(name,age));

                    class Person{
                         String name;
                         int age;
                         MyModel(this.name,this.age);
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "registerFactoryAsync(FactoryFuncAsync<T> func)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Sometimes we need to instantiate an object asynchronously, we can register those kinds of objects using this method.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "registerSingletonWithDependencies",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Registers a singleton that depends on other services already registered in get_it. Use case: When a service depends on other services to be initialized first.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "async support",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "registerSingletonAsync(): For initializing dependencies before app startup, such as an API client, shared preferences.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "registerLazySingletonAsync()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "registerFactoryParamAsync()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "instanceName parameter",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "instanceName is a keyword parameter that we can pass to any registration method while registering the instances.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If we use instanceName, our instance is registered with a name along with the type.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    final serviceLocator = GetIt.instance;
                    void setUp(){
                        serviceLocator.registerSingleton<MyWebService>(MyWebService('https://myapi.com/v1'),instanceName: 'v1');
                        serviceLocator.registerSingleton<MyWebService>(MyWebService('https://myapi.com/v2'),instanceName: 'v2');
                    }
                    var webserive = serviceLocator.get<MyWebService>(instanceName: 'v2'); //get the second registred webservice
                    """.trimIndent()
            )

            SpanText(
                text = "get_it 8.0",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // Registering a singleton (no need to specify type)
                    getIt.registerSingleton(MyService());
                    
                    // Registering a factory
                    getIt.registerFactory(() => MyOtherService());
                    
                    // Initialize
                    Future<void> setup() async {
                      await getIt.registerSingletonAsync(() async => await SomeService.initialize());
                      // Waits for the database service to be ready
                      await getIt.isReady<DatabaseService>();
                    }
                    
                     // Reset only the necessary services for testing
                    getIt.resetScope();
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import 'package:get_it/get_it.dart';

                    class DependencyManager {

                      /// GetIt instance.  
                      final getIt = GetIt.instance;

                      /// I use singleton to create this manager.
                      DependencyManager._internal();

                      static final DependencyManager _singleton = DependencyManager._internal();

                      factory DependencyManager() {
                        return _singleton;
                      }

                      T get<T extends Object>(){
                        return getIt.get<T>();
                      }

                      T getWithParam<T extends Object, P1>(dynamic p){
                        return getIt.get<T>(param1: p);
                      }

                      T getWith2Param<T extends Object, P1, P2>(dynamic p1, dynamic p2){
                        return getIt.get<T>(param1: p1, param2: p2);
                      }

                      bool isRegistered<T extends Object>(){
                        return getIt.isRegistered<T>();
                      }

                      void registerLazySingleton<T extends Object>(FactoryFunc<T> factoryFunc){
                        getIt.registerLazySingleton<T>(factoryFunc );
                      }

                      void registerFactory<T extends Object>(FactoryFunc<T> factoryFunc){
                        getIt.registerFactory<T>(factoryFunc );

                      }

                      void registerFactoryParam<T extends Object, P1, P2>(
                          FactoryFuncParam<T, P1, P2> factoryFunc, {
                            String? instanceName,
                          }){
                        getIt.registerFactoryParam<T,P1,P2>(factoryFunc);
                      }
                    }


                    void injectDependencies() {
                      try {

                        /// Get dependency manager
                        DependencyManager manager = DependencyManager();

                        /// Register UserSQLite as IUserDataSource
                        manager.registerFactory<IUserDataSource>(() => UserSQLite());

                      } catch (e2) {
                        debugPrint("Manage dependency injections error");
                      }
                    }
                    """.trimIndent()
            )
        }
    }
}


//https://regular-clock-d2b.notion.site/Get_it-Injectable-6245d15b0a914ce7a60fcc9a28a9ba05